import { Component, ElementRef, HostListener, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface TypeaheadUser {
    user_id: string;
    first_name: string;
    last_name: string;
    email: string;
    full_name?: string;
    initials?: string;
}

@Component({
    selector: 'app-typeahead-input',
    standalone: false,
    templateUrl: './typeahead-input.component.html',
    styleUrls: ['./typeahead-input.component.scss']
})
export class TypeaheadInputComponent implements OnInit, OnDestroy, OnChanges {
    @Input() existingUserList: TypeaheadUser[] = [];
    @Input() placeholder = 'Search users...';
    @Input() minChars = 3;
    @Input() maxVisibleChips = 3;
    @Input() suggestions: TypeaheadUser[] = [];
    @Input() isLoading = false;
    @Input() noResultsFound = false;
    /** Pre-populates the selected chips, e.g. to restore pending invites when
     * the dialog is reopened. Applied once on first change. */
    @Input() initialSelectedUsers: TypeaheadUser[] = [];
    @Output() selectedUsersChange = new EventEmitter<TypeaheadUser[]>();
    @Output() searchChange = new EventEmitter<string>();
    @Input() disabled: boolean = false;
    searchText = '';
    selectedUsers: TypeaheadUser[] = [];
    showSuggestions = false;
    isFocused = false;

    private readonly searchSubject = new Subject<string>();
    private readonly destroy$ = new Subject<void>();

    constructor(private readonly elRef: ElementRef) {}

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.isFocused) return;
        const target = event.target as HTMLElement;
        // When a chip is removed, the button is detached from the DOM before
        // this handler runs, so contains() returns false. Check whether the
        // target is still connected to the document — if not, it was a chip
        // removal and we should keep focus.
        if (!target.isConnected) return;
        if (!this.elRef.nativeElement.contains(target)) {
            this.isFocused = false;
            this.showSuggestions = false;
        }
    }

    get visibleUsers(): TypeaheadUser[] {
        if (this.isFocused) return this.selectedUsers;
        return this.selectedUsers.slice(0, this.maxVisibleChips);
    }

    get hiddenUsersCount(): number {
        if (this.isFocused) return 0;
        return Math.max(0, this.selectedUsers.length - this.maxVisibleChips);
    }

    get hiddenUsersTooltip(): string {
        return this.selectedUsers
            .slice(this.maxVisibleChips)
            .map(u => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || '')
            .join(', ');
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['suggestions'] || changes['noResultsFound']) {
            this.showSuggestions = this.searchText.length >= this.minChars;
        }

        // Seed the chips from a parent-provided list (restored pending invites).
        if (
            changes['initialSelectedUsers'] &&
            this.initialSelectedUsers?.length &&
            this.selectedUsers.length === 0
        ) {
            this.selectedUsers = [...this.initialSelectedUsers];
        }
    }

    ngOnInit(): void {
        this.searchSubject
            .pipe(takeUntil(this.destroy$))
            .subscribe(query => this.searchChange.emit(query));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onInputChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.searchText = input.value;

        if (this.searchText.length >= this.minChars) {
            this.searchSubject.next(this.searchText);
        } else {
            this.showSuggestions = false;
        }
    }

    // Filter out already selected users from suggestions
    getFilteredSuggestions(): TypeaheadUser[] {
        return this.suggestions.filter(user => 
            !this.selectedUsers.some(s => s.user_id === user.user_id)
        );
    }

    selectUser(user: TypeaheadUser): void {
        if(this.isExistedUser(user)) {
            this.onInputFocus();
            return;
        }
        this.selectedUsers.push(user);
        this.selectedUsersChange.emit(this.selectedUsers);
        this.searchText = '';
        this.showSuggestions = false;
    }

    removeUser(user: TypeaheadUser): void {
        this.selectedUsers = this.selectedUsers.filter(u => u.user_id !== user.user_id);
        this.selectedUsersChange.emit(this.selectedUsers);
    }

    onInputFocus(): void {
        this.isFocused = true;
        if (this.searchText.length >= this.minChars && this.suggestions.length > 0) {
            this.showSuggestions = true;
        }
    }

    onInputBlur(): void {
        // Delay to allow click on suggestion or chip removal
        setTimeout(() => {
            this.showSuggestions = false;
        }, 200);
    }

    getInitials(user: TypeaheadUser): string {
        // Use BE initials if available
        if (user.initials) {
            return user.initials;
        }
        // Fallback: generate from name/email
        const first = user.first_name?.charAt(0) || '';
        const last = user.last_name?.charAt(0) || '';
        return `${first}${last}`.toUpperCase() || '';
    }

    getDisplayName(user: TypeaheadUser): string {
        const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
        return name || '';
    }

    clearSelection(): void {
        this.selectedUsers = [];
        this.searchText = '';
        this.showSuggestions = false;
        this.selectedUsersChange.emit(this.selectedUsers);
    }

    isExistedUser(user: TypeaheadUser): boolean {
        return !!this.existingUserList.some(x=> x.user_id === user.user_id);
    }
}
